/**
 * Formatting helpers, in one place.
 *
 * `relativeTime` in particular existed as three near-identical private copies
 * (Explorer, the dashboards list, the report header) that had already started to
 * disagree about when to switch to an absolute date.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now" · "12m ago" · "3h ago" · "6d ago" · "12 Mar 2026" */
export function relativeTime(iso?: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) return `${Math.round(diff / MINUTE)}m ago`;
  if (diff < DAY) return `${Math.round(diff / HOUR)}h ago`;
  const days = Math.round(diff / DAY);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The full timestamp, for a `title` beside a relative one. */
export function absoluteTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** Milliseconds since an ISO timestamp, or `0` when absent — for sorting. */
export function timestamp(iso?: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** 1234567 → "1.2M". Keeps a KPI readable at tile size. */
export function compactNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Full precision with thousands separators — for KPI cards with room. */
export function fullNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2 });
}

/** `monthly_data[].present_days` → "Present days" */
export function humanizeField(path?: string): string {
  if (!path) return '';
  const leaf = path.replace('[].', '.').split('.').pop() || path;
  return leaf
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** `stacked_bar` → "Stacked bar" */
export function humanizeType(type?: string): string {
  if (!type) return '';
  const spaced = type.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "N thing" / "N things" without the `s`-ternary at every call site. */
export function plural(count: number, singular: string, pluralForm?: string): string {
  return `${count} ${count === 1 ? singular : (pluralForm ?? `${singular}s`)}`;
}

/** A user identity that may be an email, a uuid, or nothing. */
export function shortActor(actor?: string | null): string {
  if (!actor) return '';
  return actor.includes('@') ? actor : actor.slice(0, 8);
}
